import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FileText, Link2, Sparkles, Upload, AlertTriangle, RefreshCw, X } from 'lucide-react'
import {
  projectsApi,
  AiSuggestionRow,
  AutoMapSessionStatus,
  ProjectComplianceProfile,
  ProjectSystem,
  RaDocumentRow,
  RaSystemAssessmentRow,
} from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { Button } from '@/components/ui/Button'
import { ImportRaWordModal } from '@/components/projects/ImportRaWordModal'
import { WordRaMappingsPanel } from '@/components/projects/WordRaMappingsPanel'
import { AutoMapReviewPane } from '@/components/projects/AutoMapReviewPane'
import { useTranslation } from 'react-i18next'

interface Props {
  projectId:     string
  canEdit:       boolean
  complianceProfiles: ProjectComplianceProfile[]
  raDocuments:   RaDocumentRow[]
  onRefreshDocs: () => void
}

type WordRaSubTab = 'documents' | 'mappings' | 'ai-suggestions'

// project-level Word RA hub. three sub-tabs share state here so an
// auto-map run (kicked off in Mappings) can land its result in AI
// Suggestions without re-fetching. the Mappings sub-tab still owns its
// own table data (filters, dirty diff). the AI Suggestions surface reads
// from the lookups loaded here so it can label rows consistently
export const WordRaProjectTab: React.FC<Props> = ({
  projectId,
  canEdit,
  complianceProfiles,
  raDocuments,
  onRefreshDocs,
}) => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const subParam = searchParams.get('sub')

  // default sub-tab: 'documents' when empty (upload CTA is the landing
  // surface), 'mappings' once a doc exists
  const defaultSub: WordRaSubTab = raDocuments.length === 0 ? 'documents' : 'mappings'

  const activeSubTab: WordRaSubTab =
    subParam === 'documents'      ? 'documents'      :
    subParam === 'mappings'       ? 'mappings'       :
    subParam === 'ai-suggestions' ? 'ai-suggestions' :
                                    defaultSub

  const setActiveSubTab = (sub: WordRaSubTab) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', 'word-ra')
    next.set('sub', sub)
    setSearchParams(next, { replace: true })
  }

  // doc selector. most projects have a single ra_document, but the schema
  // allows multiple revisions. default to the most recently parsed one
  // (first in the list, per ra_documents query order)
  const [selectedDocId, setSelectedDocId] = useState<string | null>(raDocuments[0]?.id ?? null)
  useEffect(() => {
    // if raDocuments changes (e.g. a fresh upload arrived) and we have no
    // selection or the selection is stale, pick the most recent
    if (!selectedDocId || !raDocuments.find(d => d.id === selectedDocId)) {
      setSelectedDocId(raDocuments[0]?.id ?? null)
    }
  }, [raDocuments, selectedDocId])

  // lookup maps for the review pane. loaded once per selected doc,
  // refreshed after the user accepts suggestions so the next render
  // reflects the post-PATCH state
  const [projectSystemsById, setProjectSystemsById] = useState<Map<string, ProjectSystem>>(new Map())
  const [, setAssessmentsById] = useState<Map<string, RaSystemAssessmentRow>>(new Map())
  const [lookupsReady,       setLookupsReady]       = useState(false)

  // persisted suggestions for the AI Suggestions sub-tab. Phase E1.5 moved
  // this from React state (terminal-status snapshot) to the DB. the sub-tab
  // refetches whenever it becomes active, after a per-row action, or after
  // a fresh auto-map run completes
  const [pendingSuggestions, setPendingSuggestions] = useState<AiSuggestionRow[]>([])
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)

  // Phase E1.5 resume: on mount and on doc switch, ask the backend whether
  // there's a running session for the selected doc. if so, hand the
  // session_id down to WordRaMappingsPanel so the progress modal re-attaches
  // without the user re-triggering Auto-map
  const [resumeSessionId, setResumeSessionId] = useState<string | null>(null)
  useEffect(() => {
    if (!selectedDocId || !canEdit) { setResumeSessionId(null); return }
    let cancel = false
    projectsApi.getRunningAiSession(selectedDocId)
      .then(res => {
        if (cancel) return
        // only attach if state is still 'running'. the resume endpoint
        // auto-fails stale sessions (>30 min no progress) but returns them
        // with state='failed', which the modal would just close instantly.
        // better to ignore those
        if (res.session && res.session.state === 'running') {
          setResumeSessionId(res.session.id)
        } else {
          setResumeSessionId(null)
        }
      })
      .catch(() => { if (!cancel) setResumeSessionId(null) })
    return () => { cancel = true }
  }, [selectedDocId, canEdit])

  const refetchSuggestions = useCallback(async () => {
    if (!selectedDocId) return
    setSuggestionsLoading(true)
    try {
      const res = await projectsApi.getAiSuggestions(selectedDocId, 'pending')
      setPendingSuggestions(res.suggestions)
    } catch {
      setPendingSuggestions([])
    } finally {
      setSuggestionsLoading(false)
    }
  }, [selectedDocId])

  // eager-load lookups when there's a doc and the user is heading toward
  // Mappings (for cross-tab refresh consistency) or AI Suggestions
  const needsLookups = selectedDocId && (activeSubTab === 'ai-suggestions' || activeSubTab === 'mappings')
  useEffect(() => {
    if (!needsLookups) return
    let cancel = false
    Promise.all([
      projectsApi.getRaDocument(selectedDocId!),
      projectsApi.listProjectSystems(projectId),
    ]).then(([raDoc, psList]) => {
      if (cancel) return
      const aMap = new Map<string, RaSystemAssessmentRow>()
      for (const a of raDoc.assessments) aMap.set(a.id, a)
      const psMap = new Map<string, ProjectSystem>()
      for (const ps of psList) psMap.set(ps.id, ps)
      setAssessmentsById(aMap)
      setProjectSystemsById(psMap)
      setLookupsReady(true)
    }).catch(() => { if (!cancel) setLookupsReady(false) })
    return () => { cancel = true }
  }, [needsLookups, selectedDocId, projectId])

  // refetch suggestions on AI Suggestions sub-tab activation
  useEffect(() => {
    if (activeSubTab !== 'ai-suggestions') return
    refetchSuggestions()
  }, [activeSubTab, selectedDocId, refetchSuggestions])

  const subTabs = useMemo(() => ([
    { key: 'documents'      as const, label: t('wordRa.documents'),     icon: FileText, count: raDocuments.length },
    { key: 'mappings'       as const, label: t('wordRa.mappings'),      icon: Link2,    count: null as number | null },
    { key: 'ai-suggestions' as const, label: t('wordRa.aiSuggestions'), icon: Sparkles, count: pendingSuggestions.length },
  ]), [raDocuments.length, pendingSuggestions.length, t])

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
      {/* Sub-tab nav */}
      <div className="border-b border-slate-200 dark:border-slate-700 mb-4">
        <div className="flex gap-5 -mb-px">
          {subTabs.map(t => {
            const Icon   = t.icon
            const active = activeSubTab === t.key
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveSubTab(t.key)}
                className={
                  'pb-2 pt-1 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ' +
                  (active
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200')
                }
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
                {t.count !== null && t.count > 0 && (
                  <span className="ml-0.5 text-xs font-normal text-slate-400">({t.count})</span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {activeSubTab === 'documents' && (
        <DocumentsSubTab
          projectId={projectId}
          canEdit={canEdit}
          complianceProfiles={complianceProfiles}
          raDocuments={raDocuments}
          onRefreshDocs={onRefreshDocs}
        />
      )}

      {activeSubTab === 'mappings' && (
        selectedDocId ? (
          <div className="flex flex-col h-[calc(100vh-260px)] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            {raDocuments.length > 1 && (
              <DocSelector
                raDocuments={raDocuments}
                selectedDocId={selectedDocId}
                onChange={setSelectedDocId}
              />
            )}
            <WordRaMappingsPanel
              raDocumentId={selectedDocId}
              projectId={projectId}
              canEdit={canEdit}
              resumeSessionId={resumeSessionId}
              onSaved={() => {/* stay on Mappings. the panel reloads in place */}}
              onAutoMapTerminal={(_status: AutoMapSessionStatus) => {
                // suggestions live in the DB now. the sub-tab refetches on
                // activation. just switch to that surface
                setResumeSessionId(null)
                setActiveSubTab('ai-suggestions')
                refetchSuggestions()
              }}
            />
          </div>
        ) : (
          <EmptyDocsState onGoDocuments={() => setActiveSubTab('documents')} />
        )
      )}

      {activeSubTab === 'ai-suggestions' && (
        !selectedDocId ? (
          <EmptyDocsState onGoDocuments={() => setActiveSubTab('documents')} />
        ) : suggestionsLoading && pendingSuggestions.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-400">{t('wordRa.loadingSuggestions')}</div>
        ) : pendingSuggestions.length === 0 ? (
          <EmptyAiState onGoMappings={() => setActiveSubTab('mappings')} />
        ) : !lookupsReady ? (
          <div className="p-12 text-center text-sm text-slate-400">{t('wordRa.loadingProjectSystems')}</div>
        ) : (
          <div className="flex flex-col h-[calc(100vh-260px)] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <AutoMapReviewPane
              raDocumentId={selectedDocId}
              projectId={projectId}
              canEdit={canEdit}
              suggestions={pendingSuggestions}
              projectSystemsById={projectSystemsById}
              onBack={() => setActiveSubTab('mappings')}
              onChanged={() => {
                // action(s) committed, refetch the pending list. accepted
                // rows hit the mappings table, so refresh that surface's
                // source-of-truth too
                refetchSuggestions()
                onRefreshDocs()
              }}
            />
          </div>
        )
      )}
    </div>
  )
}

// ─── Documents sub-tab ────────────────────────────────────────────────────

const DocumentsSubTab: React.FC<{
  projectId:     string
  canEdit:       boolean
  complianceProfiles: ProjectComplianceProfile[]
  raDocuments:   RaDocumentRow[]
  onRefreshDocs: () => void
}> = ({ projectId, canEdit, complianceProfiles, raDocuments, onRefreshDocs }) => {
  const { t, i18n } = useTranslation()
  const { success, error: showError } = useToast()
  const [importOpen,    setImportOpen]    = useState(false)
  // confirm-modal target. null when no modal open. a doc id when the user
  // clicked Re-parse and we're waiting for confirmation
  const [reparseTarget, setReparseTarget] = useState<RaDocumentRow | null>(null)
  // per-doc spinner: id of the doc currently being re-parsed (or null).
  // disables the row's Re-parse button and shows "Re-parsing…" so a
  // double-click can't double-fire
  const [reparseBusy,   setReparseBusy]   = useState<string | null>(null)
  // #3 delete-confirm target + per-doc spinner
  const [deleteTarget, setDeleteTarget] = useState<RaDocumentRow | null>(null)
  const [deleteBusy,   setDeleteBusy]   = useState<string | null>(null)

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    const target = deleteTarget
    setDeleteBusy(target.id)
    setDeleteTarget(null)
    try {
      const res = await projectsApi.deleteRaDocument(target.id)
      success(
        `Deleted document — ${res.assessments} assessment${res.assessments === 1 ? '' : 's'} and mappings removed` +
        (res.word_ra_inventory_removed ? `, ${res.word_ra_inventory_removed} inventory row(s) cleared` : '') + '.',
      )
      window.dispatchEvent(new CustomEvent('seacuredb:ra-mappings-updated'))
      onRefreshDocs()
    } catch (err: any) {
      showError(err?.message ?? 'Delete failed')
    } finally {
      setDeleteBusy(null)
    }
  }

  const handleReparseConfirm = async () => {
    if (!reparseTarget) return
    const target = reparseTarget
    setReparseBusy(target.id)
    setReparseTarget(null)
    try {
      const res = await projectsApi.reparseRaDocument(target.id)
      success(
        `Re-parsed: ${res.details_replaced} detail${res.details_replaced === 1 ? '' : 's'}, ` +
        `${res.diagrams_replaced} diagram${res.diagrams_replaced === 1 ? '' : 's'} replaced`,
      )
      // tell any open System Detail Word RA tab to refetch. the same event
      // already drives post-PATCH refreshes. re-parse swaps detail rows
      // under the hood so existing views must reload
      window.dispatchEvent(new CustomEvent('seacuredb:ra-mappings-updated'))
      onRefreshDocs()
    } catch (err: any) {
      // surface the server's error message verbatim. the 400 case ("No
      // source file stored. Delete and re-upload manually.") is actionable
      // and the user needs to see it
      showError(err?.message ?? 'Re-parse failed')
    } finally {
      setReparseBusy(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t('wordRa.documents')}</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Word RA documents parsed for this project. Re-parse re-runs the current parser against
            the stored source file. Delete removes the document and all its assessments, mappings,
            and diagrams.
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setImportOpen(true)} size="sm">
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            {t('common.upload')} Word RA
          </Button>
        )}
      </div>

      {raDocuments.length === 0 ? (
        <div className="bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-12 text-center">
          <FileText className="h-9 w-9 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
          <div className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{t('wordRa.noEvidence')}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-4">
            Upload the class-society Risk Assessment .docx to ingest the master table, per-system
            detailed sections, and diagrams.
          </div>
        </div>
      ) : (
        <ul className="space-y-2">
          {raDocuments.map(d => (
            <li key={d.id} className="bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="text-sm font-medium text-slate-900 dark:text-white truncate flex-1">
                  {d.vessel_name ?? d.project_name ?? 'Risk Assessment'}
                </div>
                {d.class_society && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium">
                    {d.class_society}
                  </span>
                )}
                {d.revision && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                    Rev. {d.revision}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                {t('wordRa.parsed')} {new Date(d.parsed_at).toLocaleString(i18n.resolvedLanguage)}
                {d.applicable_rules && d.applicable_rules.length > 0 && (
                  <> · {d.applicable_rules.length} applicable rule{d.applicable_rules.length === 1 ? '' : 's'}</>
                )}
              </div>
              <div className="mt-2 flex items-center gap-3 text-[11px]">
                <button disabled className="text-slate-400 cursor-not-allowed" title={t('wordRa.pending')}>{t('common.view')}</button>
                <span className="text-slate-300">·</span>
                {canEdit && (reparseBusy === d.id ? (
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    <RefreshCw className="h-3 w-3 animate-spin" />
                    Re-parsing…
                  </span>
                ) : (
                  <button
                    onClick={() => setReparseTarget(d)}
                    disabled={reparseBusy !== null}
                    className="text-blue-600 dark:text-blue-400 hover:underline disabled:text-slate-400 disabled:no-underline disabled:cursor-not-allowed inline-flex items-center gap-1"
                    title={t('wordRa.reparseHint')}
                  >
                    <RefreshCw className="h-3 w-3" />
                    Re-parse
                  </button>
                ))}
                {canEdit && <span className="text-slate-300">·</span>}
                {canEdit && (deleteBusy === d.id ? (
                  <span className="text-slate-500">{t('wordRa.delete')}…</span>
                ) : (
                  <button
                    onClick={() => setDeleteTarget(d)}
                    disabled={deleteBusy !== null || reparseBusy !== null}
                    className="text-red-600 dark:text-red-400 hover:underline disabled:text-slate-400 disabled:no-underline disabled:cursor-not-allowed"
                    title={t('wordRa.deleteDocumentHint')}
                  >
                    {t('common.delete')}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}

      {canEdit && importOpen && (
        <ImportRaWordModal
          projectId={projectId}
          complianceProfiles={complianceProfiles}
          onClose={() => setImportOpen(false)}
          onCommitted={() => {
            setImportOpen(false)
            onRefreshDocs()
          }}
        />
      )}

      {canEdit && reparseTarget && (
        <ReparseConfirmModal
          doc={reparseTarget}
          onCancel={() => setReparseTarget(null)}
          onConfirm={handleReparseConfirm}
        />
      )}

      {canEdit && deleteTarget && (
        <DeleteRaConfirmModal
          doc={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  )
}

// #3 typed-confirm delete dialog. the blast radius (all assessments,
// mappings, diagrams, auto-inventory) warrants making the user type DELETE
// before the button enables
const DeleteRaConfirmModal: React.FC<{
  doc: RaDocumentRow
  onCancel: () => void
  onConfirm: () => void
}> = ({ doc, onCancel, onConfirm }) => {
  const { t } = useTranslation()
  const [typed, setTyped] = useState('')
  const armed = typed.trim().toUpperCase() === 'DELETE'
  const label = [doc.vessel_name, doc.class_society, doc.revision].filter(Boolean).join(' · ') || 'this document'
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-5">
        <h2 className="text-base font-semibold text-red-600 dark:text-red-400 mb-2">{t('common.delete')} RA</h2>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          This permanently removes <span className="font-medium">{label}</span> and every assessment,
          mapping, detailed section, and diagram parsed from it, plus any inventory rows it auto-wrote.
          Mapped systems are kept (only their Word-RA link is cleared). This cannot be undone.
        </p>
        <label className="block text-xs text-slate-500 dark:text-slate-400 mt-4 mb-1">
          {t('wordRa.typeDelete')} <span className="font-mono font-semibold">DELETE</span>
        </label>
        <input
          autoFocus
          value={typed}
          onChange={e => setTyped(e.target.value)}
          className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200">
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={!armed}
            className="px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Doc selector for projects with >1 ra_document ────────────────────────

const DocSelector: React.FC<{
  raDocuments:   RaDocumentRow[]
  selectedDocId: string
  onChange:      (id: string) => void
}> = ({ raDocuments, selectedDocId, onChange }) => {
  const { t } = useTranslation()
  return (
  <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/30 flex items-center gap-2 text-xs">
    <span className="text-slate-500">{t('common.view')}:</span>
    <select
      value={selectedDocId}
      onChange={e => onChange(e.target.value)}
      className="text-xs border border-slate-300 dark:border-slate-600 dark:bg-slate-800 rounded px-2 py-1"
    >
      {raDocuments.map(d => (
        <option key={d.id} value={d.id}>
          {d.vessel_name ?? d.project_name ?? d.id.slice(0, 8)}
          {d.class_society ? ` · ${d.class_society}` : ''}
          {d.revision ? ` · Rev. ${d.revision}` : ''}
        </option>
      ))}
    </select>
  </div>
  )
}

// ─── Re-parse confirmation ────────────────────────────────────────────────

const ReparseConfirmModal: React.FC<{
  doc:       RaDocumentRow
  onCancel:  () => void
  onConfirm: () => void
}> = ({ doc, onCancel, onConfirm }) => {
  const { t } = useTranslation()
  return (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-5 space-y-3">
      <div className="flex items-center gap-2">
        <RefreshCw className="h-4 w-4 text-blue-500" />
        <h3 className="text-base font-semibold">{t('wordRa.reparseTitle')}</h3>
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-300">
        The stored source file for{' '}
        <strong>{doc.vessel_name ?? doc.project_name ?? 'this RA document'}</strong>
        {doc.class_society ? <> ({doc.class_society})</> : null}
        {' '}will be re-processed with the current parser. Detailed RA content and diagrams will be
        replaced. System mappings and AI suggestions are preserved.
      </p>
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel}>
          <X className="h-3.5 w-3.5 mr-1.5" />
          {t('common.cancel')}
        </Button>
        <Button size="sm" onClick={onConfirm}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Re-parse
        </Button>
      </div>
    </div>
  </div>
  )
}

// ─── Empty states ─────────────────────────────────────────────────────────

const EmptyDocsState: React.FC<{ onGoDocuments: () => void }> = ({ onGoDocuments }) => {
  const { t } = useTranslation()
  return (
  <div className="bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-12 text-center">
    <AlertTriangle className="h-9 w-9 text-amber-400 mx-auto mb-2" />
    <div className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{t('wordRa.noRows')}</div>
    <div className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-4">
      Upload a Word Risk Assessment to begin mapping its rows to existing project systems.
    </div>
    <Button onClick={onGoDocuments} size="sm" variant="outline">
      {t('wordRa.documents')}
    </Button>
  </div>
  )
}

const EmptyAiState: React.FC<{ onGoMappings: () => void }> = ({ onGoMappings }) => {
  const { t } = useTranslation()
  return (
  <div className="bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-12 text-center">
    <Sparkles className="h-9 w-9 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
    <div className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{t('wordRa.noChanges')}</div>
    <div className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-4">
      {t('wordRa.aiEmptyHint')}
    </div>
    <Button onClick={onGoMappings} size="sm" variant="outline">
      {t('wordRa.mappings')}
    </Button>
  </div>
  )
}
